// components/features/support/clients/ClientForm.tsx
import { useState, useEffect } from "react";
import { alertError } from "../../../helpers/alertError";

// Liste des pays (exportée pour réutilisation)
export const countriesList = [
  { code: "CM", name: "Cameroun", phoneCode: "+237" },
  { code: "CI", name: "Côte d'Ivoire", phoneCode: "+225" },
  { code: "SN", name: "Sénégal", phoneCode: "+221" },
  { code: "FR", name: "France", phoneCode: "+33" },
  { code: "CA", name: "Canada", phoneCode: "+1" },
  { code: "US", name: "États-Unis", phoneCode: "+1" },
  { code: "GB", name: "Royaume-Uni", phoneCode: "+44" },
  { code: "BE", name: "Belgique", phoneCode: "+32" },
  { code: "CH", name: "Suisse", phoneCode: "+41" },
  { code: "LU", name: "Luxembourg", phoneCode: "+352" },
  { code: "DE", name: "Allemagne", phoneCode: "+49" },
  { code: "IT", name: "Italie", phoneCode: "+39" },
  { code: "ES", name: "Espagne", phoneCode: "+34" },
  { code: "PT", name: "Portugal", phoneCode: "+351" },
  { code: "MA", name: "Maroc", phoneCode: "+212" },
  { code: "TN", name: "Tunisie", phoneCode: "+216" },
  { code: "DZ", name: "Algérie", phoneCode: "+213" },
  { code: "ML", name: "Mali", phoneCode: "+223" },
  { code: "BF", name: "Burkina Faso", phoneCode: "+226" },
  { code: "NE", name: "Niger", phoneCode: "+227" },
  { code: "TG", name: "Togo", phoneCode: "+228" },
  { code: "BJ", name: "Bénin", phoneCode: "+229" },
  { code: "GA", name: "Gabon", phoneCode: "+241" },
  { code: "CG", name: "Congo", phoneCode: "+242" },
  { code: "CD", name: "RDC", phoneCode: "+243" },
];

export interface ClientFormData {
  firstname: string;
  lastname: string;
  email?: string;
  country?: string;
  country_code?: string;
  phone: string;
}

interface ClientFormProps {
  initialData?: Partial<ClientFormData>;
  onSubmit: (data: ClientFormData) => void;
  onCancel: () => void;
  loading?: boolean;
  submitLabel?: string;
  cancelLabel?: string;
  title?: string;
}

export default function ClientForm({
  initialData = {},
  onSubmit,
  onCancel,
  loading = false,
  submitLabel = "Créer le client",
  cancelLabel = "Annuler",
  title,
}: ClientFormProps) {
  const [formData, setFormData] = useState<ClientFormData>({
    firstname: "",
    lastname: "",
    email: "",
    country: "",
    country_code: "",
    phone: "",
    ...initialData,
  });

  // Mettre à jour le formulaire si les données initiales changent
  useEffect(() => {
    if (initialData) {
      setFormData((prev) => ({ ...prev, ...initialData }));
    }
  }, [initialData]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleCountryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const code = e.target.value;
    const country = countriesList.find((c) => c.code === code);
    setFormData((prev) => ({
      ...prev,
      country_code: code,
      country: country?.name || "",
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.firstname || !formData.lastname) {
      alertError("Veuillez remplir le prénom et le nom");
      return;
    }

    if (!formData.country_code) {
      alertError("Veuillez sélectionner un pays");
      return;
    }

    onSubmit(formData);
  };

  return (
    <div>
      {title && <h3 className="text-lg font-semibold mb-4">{title}</h3>}
      
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Prénom *
            </label>
            <input
              type="text"
              name="firstname"
              value={formData.firstname}
              onChange={handleChange}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent outline-none"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Nom *
            </label>
            <input
              type="text"
              name="lastname"
              value={formData.lastname}
              onChange={handleChange}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent outline-none"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Numéro de téléphone
          </label>
          <input
            type="tel"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent outline-none"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Email (optionnel)
          </label>
          <input
            type="email"
            name="email"
            value={formData.email || ""}
            onChange={handleChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent outline-none"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Pays *
          </label>
          <select
            value={formData.country_code || ""}
            onChange={handleCountryChange}
            required
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent outline-none"
          >
            <option value="">Sélectionner un pays</option>
            {countriesList.map((country) => (
              <option key={country.code} value={country.code}>
                {country.name} ({country.phoneCode})
              </option>
            ))}
          </select>
        </div>

        <div className="flex justify-end gap-3 pt-4">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
          >
            {cancelLabel}
          </button>
          <button
            type="submit"
            disabled={loading}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {loading ? "Chargement..." : submitLabel}
          </button>
        </div>
      </form>
    </div>
  );
}